import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const API_URL_2 = process.env.REACT_APP_BASE_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Error handling helper
const handleError = (error) => {
  const errorMessage = error.response?.data?.error || error.message || 'An error occurred';
  console.error('API Error:', errorMessage);
  return new Error(errorMessage);
};

// Helper function to check if token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch (e) {
    return true;
  }
};

// Request interceptor for adding auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  
  // Check if token is expired before making the request
  if (token && isTokenExpired(token) && !isRedirectingToLogin) {
    isRedirectingToLogin = true;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.replace('/login');
    throw new Error('Token expired');
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Keep track of if we're already redirecting to avoid multiple redirects
let isRedirectingToLogin = false;

// Add a response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors (token expired or invalid)
    if (error.response?.status === 401 && !isRedirectingToLogin) {
      isRedirectingToLogin = true;
      
      // Clear auth data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Force reload to login page to clear any cached states
      window.location.replace('/login');
      return new Promise(() => {}); // Block further execution
    }
    
    // Check for server connection errors
    if (!error.response || error.code === 'ERR_NETWORK') {
      window.dispatchEvent(new CustomEvent('serverError'));
    }
    
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Apps API
const appsApi = {
  getAllApps: () => api.get('/apps'),
  getAppById: (id) => api.get(`/apps/${id}`),
  extractApkMetadata: (formData) => api.post('/apps/extract-metadata', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  uploadApp: (formData, onProgress) => api.post('/apps/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onProgress?.(percentCompleted);
    },
  }),
  updateApp: (id, formData, onProgress) => api.put(`/apps/${id}/update`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onProgress?.(percentCompleted);
    },
  }),
  deleteApp: (id) => api.delete(`/apps/${id}`),
  addPictures: (id, formData) => api.post(`/apps/${id}/pictures`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  deletePictures: (id, picturePaths) => api.delete(`/apps/${id}/pictures`, { data: { picturePaths } }),

  // Delete a specific picture by index
  deleteAppPicture: (appId, pictureIndex) => api.delete(`/apps/${appId}/pictures/${pictureIndex}`),
  getOldReleases: (appId) => api.get(`/old-releases/${appId}`),
  getAppGroups: () => api.get('/app-group-connections'),
  updateAppGroups: (appId, groupId) => api.post(`/app-group-connections`, { appId, groupId }),
  removeAppGroup: (appId, groupId) => api.delete(`/app-group-connections`, { data: { appId, groupId } }),
  increaseUsers: (id) => api.patch(`/apps/${id}/increase-users`),
  decreaseUsers: (id) => api.patch(`/apps/${id}/decrease-users`),
  getAppIcon: async (iconPath) => {
    try {
      const response = await api.get(`/app-icon?path=${encodeURIComponent(iconPath)}`, {
        responseType: 'blob'
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error('Error fetching app icon:', error);
      return null;
    }
  },
};

// Groups API
const groupsApi = {
  getGroups: () => api.get('/groups'),
  createGroup: (data) => api.post('/groups', data),
  deleteGroup: (id) => api.delete(`/groups/${id}`),
  sendNotification: (groupId, message) => api.post('/notifications/send-to-group', { groupId, message }),
  getGroupUsers: (groupTag) => api.get(`/users/group-tag?groupTag=${groupTag}`),
};

// Users API
const usersApi = {
  getAllUsers: () => api.get('/users'),
  searchUsers: (query) => api.get(`/users/search?query=${query}`),
  updateUserTag: (userId, newTag) => api.patch(`/users/${userId}/tag`, { newTag }),
  changeAdminPassword: (username, oldPassword, newPassword) => {
    return api.patch(`/admin/change-password`, {
      username,
      oldPassword,
      newPassword,
    });
  },
  sendNotification: (userId, title, body) => api.post('/send-notification', { userId, title, body }),
};

// Dashboard API
const dashboardApi = {
  getStatistics: async () => {
    try {
      const [appsRes, usersRes, groupsRes] = await Promise.all([
        api.get('/apps'),
        api.get('/users'),
        api.get('/groups'),
      ]);
      
      return {
        totalApps: appsRes.data.length,
        totalUsers: usersRes.data.users.length,
        totalGroups: groupsRes.data.groups.length,
        apps: appsRes.data,
      };
    } catch (error) {
      console.error('Error fetching dashboard statistics:', error);
      return {
        totalApps: 0,
        totalUsers: 0,
        totalGroups: 0,
        apps: [],
      };
    }
  },
};

// Admin API
const adminApi = {
  login: (username, password) => api.post('/login', { username, password }),
  verifyOtp: (username, otpCode) => api.post('/admin/verify-otp', { username, otpCode }),
  addAdmin: (data) => api.post('/admin/add', data),
  changePassword: (data) => api.patch('/admin/change-password', data),
};

// File Management
const fileManagementApi = {
  listFiles: async (path = '') => {
    try {
      const response = await api.get('/files', { params: { path } });
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  downloadFile: async (filename, preview = false) => {
    try {
      // Clean up the path by removing any double slashes and ensuring it starts with a single slash
      const cleanPath = filename.split('/').filter(Boolean).join('/');
      const path = `/${cleanPath}`;
      
      const response = await api.get(`/files/download`, {
        params: { path },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      if (preview) {
        return url;
      }

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename.split('/').pop());
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw handleError(error);
    }
  },

  deleteFile: async (filename) => {
    try {
      // Clean up the path by removing any double slashes and ensuring it starts with a single slash
      const cleanPath = filename.split('/').filter(Boolean).join('/');
      const path = `/${cleanPath}`;
      
      const response = await api.delete(`/files/delete`, {
        params: { path }
      });
      return response.data;
    } catch (error) {
      throw handleError(error);
    }
  },
};

export { api, appsApi, groupsApi, usersApi, dashboardApi, adminApi, fileManagementApi };
